// dependencies
import React from "react";
import { List } from "immutable";
import PropTypes from "prop-types";
import { connect } from "react-redux";

// components
import Button from "Components/Button";
import Request from "Components/Request";
import Spinner from "Components/Spinner";
import Paginator from "Components/Paginator";
import TextInput from "Components/Inputs/Text";
import RadioInput from "Components/Inputs/Radio";

// selectors
import { getIsRequesting } from "Selectors/app";
import {
	getRequests,
	getCurrentPage,
	getRequestsCount,
	getPageSize,
	getFiltersMap,
	getMethodOptions,
	getAuthenticatedOptions,
	getPossessedOptions,
} from "./selectors";

// actions
import {
	init,
	fetchRequests,
	changePage,
	urlSearch,
	methodChange,
	authenticatedChange,
	possessedChange,
	requestIDSearch,
} from "./actions";

// css
import css from "./RequestsPage";

class RequestsPage extends React.PureComponent {
	static propTypes = {
		isRequesting: PropTypes.bool.isRequired,
		init: PropTypes.func.isRequired,
		changePage: PropTypes.func.isRequired,
		urlSearch: PropTypes.func.isRequired,
		methodChange: PropTypes.func.isRequired,
		authenticatedChange: PropTypes.func.isRequired,
		possessedChange: PropTypes.func.isRequired,
		fetchRequests: PropTypes.func.isRequired,
		requestIDSearch: PropTypes.func.isRequired,
		currentPage: PropTypes.number.isRequired,
		requestsCount: PropTypes.number.isRequired,
		pageSize: PropTypes.number.isRequired,
		environment: PropTypes.string.isRequired,
		filterRequestID: PropTypes.string.isRequired,
		filterURL: PropTypes.string.isRequired,
		filterMethod: PropTypes.string.isRequired,
		filterPossessed: PropTypes.string.isRequired,
		filterAuthenticated: PropTypes.string.isRequired,
		possessedOptions: PropTypes.instanceOf(List).isRequired,
		requests: PropTypes.instanceOf(List).isRequired,
		methodOptions: PropTypes.instanceOf(List).isRequired,
		authenticatedOptions: PropTypes.instanceOf(List).isRequired,
	};

	static defaultProps = {};

	componentDidMount() {
		this.props.init();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.environment !== this.props.environment) {
			this.props.init();
		}
	}

	changePage = (page) => {
		this.props.changePage(page);
	};

	handleSearch = () => {
		this.props.fetchRequests();
	};

	handleURLSearch = (value) => {
		this.props.urlSearch(value);
	};

	handleRequestIDChange = (value) => {
		this.props.requestIDSearch(value);
	};

	handleMethodChange = (value) => {
		this.props.methodChange(value);
	};

	handleAuthenticatedChange = (value) => {
		this.props.authenticatedChange(value);
	};

	handlePossessedChange = (value) => {
		this.props.possessedChange(value);
	};

	render() {
		const {
			requests,
			pageSize,
			filterURL,
			currentPage,
			isRequesting,
			filterMethod,
			requestsCount,
			methodOptions,
			filterRequestID,
			filterPossessed,
			possessedOptions,
			filterAuthenticated,
			authenticatedOptions,
		} = this.props;
		const pageCount = Math.ceil(requestsCount / pageSize);

		// determine if we are waiting on new results rather than a page change
		const waitingOnNewResults = requests.size === 0 && isRequesting;

		return (
			<div className={css.RequestsPageContainer}>
				<div className={css.filterWrapper}>
					<div className={css.filterContainer}>
						<div>
							<div className={css.inputContainer}>
								<div className={css.label}>Request ID</div>
								<TextInput value={filterRequestID} onChange={this.handleRequestIDChange} />
							</div>

							<div className={css.inputContainer}>
								<div className={css.label}>URL</div>
								<TextInput value={filterURL} onChange={this.handleURLSearch} />
							</div>

							<div className={css.inputContainer}>
								<div className={css.label}>Method</div>
								<RadioInput
									value={filterMethod}
									options={methodOptions}
									onChange={this.handleMethodChange}
								/>
							</div>

							<div className={css.inputContainer}>
								<div className={css.label}>Authenticated</div>
								<RadioInput
									value={filterAuthenticated}
									options={authenticatedOptions}
									onChange={this.handleAuthenticatedChange}
								/>
							</div>

							<div className={css.inputContainer}>
								<div className={css.label}>Possessed</div>
								<RadioInput
									value={filterPossessed}
									options={possessedOptions}
									onChange={this.handlePossessedChange}
								/>
							</div>

							<div className={css.inputContainer}>
								<Button onClick={this.handleSearch}>Search</Button>
							</div>
						</div>
					</div>
				</div>

				<div className={css.mainContent}>
					<div className={css.pageHead}>
						<div className={css.pageTitle}>Recent Requests</div>
						{waitingOnNewResults ? null : (
							<div className={css.subTitle}>{`About ${requestsCount} requests.`}</div>
						)}
					</div>

					<div className={css.requestsList}>
						{waitingOnNewResults ? (
							<Spinner centered />
						) : (
							requests.map((request) => <Request key={request.get("id")} request={request} />)
						)}
					</div>

					<div className={css.paginatorWrapper}>
						<Paginator
							isRequesting={isRequesting}
							onClick={this.changePage}
							pageCount={pageCount}
							currentPage={currentPage}
						/>
					</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const filters = getFiltersMap(state);

	return {
		requests: getRequests(state),
		pageSize: getPageSize(state),
		filterURL: filters.get("url"),
		filterMethod: filters.get("method"),
		currentPage: getCurrentPage(state),
		requestsCount: getRequestsCount(state),
		methodOptions: getMethodOptions(state),
		filterRequestID: filters.get("requestID"),
		filterPossessed: filters.get("possessed"),
		possessedOptions: getPossessedOptions(state),
		filterAuthenticated: filters.get("authenticated"),
		authenticatedOptions: getAuthenticatedOptions(state),
		isRequesting: getIsRequesting(state, "REQUESTS:GET"),
	};
};

const mapDispatchToProps = (dispatch) => ({
	init: () => dispatch(init()),
	fetchRequests: () => dispatch(fetchRequests()),
	urlSearch: (value) => dispatch(urlSearch(value)),
	requestIDSearch: (value) => dispatch(requestIDSearch(value)),
	changePage: (pageNumber) => dispatch(changePage(pageNumber)),
	methodChange: (pageNumber) => dispatch(methodChange(pageNumber)),
	authenticatedChange: (pageNumber) => dispatch(authenticatedChange(pageNumber)),
	possessedChange: (pageNumber) => dispatch(possessedChange(pageNumber)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RequestsPage);
